import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack, Grid } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 *
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  const cartProductIds = cartData.map((product) => product.productId);
  const finalCart = [];
  for (let i = 0; i < cartProductIds.length; i++) {
    const cart = productsData.filter((product) => {
      return product._id === cartProductIds[i];
    })[0];
    finalCart.push({ ...cart, qty: cartData[i].qty });
  }
  return finalCart;
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  return items.reduce((accumulator, item) => {
    return accumulator + item.qty * item.cost;
  }, 0);
};

const getTotalItems = (items = []) => {
  return items.reduce((accumulator, item) => {
    return accumulator + item.qty;
  }, 0);
};

/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 *
 */
const ItemQuantity = ({ value, handleAdd, handleDelete, isReadOnly }) => {
  if (isReadOnly) {
    return (
      <Stack direction="row" alignItems="center">
        <Box padding="0.5rem" data-testid="item-qty">
          Qty: {value}
        </Box>
      </Stack>
    );
  }
  return (
    <Stack direction="row" alignItems="center">
      <IconButton size="small" color="primary" onClick={handleDelete}>
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton size="small" color="primary" onClick={handleAdd}>
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 *
 */
const Cart = ({ products, items = [], handleQuantity, isReadOnly }) => {
  let history = useHistory();

  const handleAdd = (item) => {
    const quantity = item.qty + 1;
    handleQuantity(item._id, quantity, {
      preventDuplicate: false,
      handleAdd: true,
      handleDelete: false,
    });
  };
  const handleDelete = (item) => {
    const quantity = item.qty - 1;
    handleQuantity(item._id, quantity, {
      preventDuplicate: false,
      handleAdd: false,
      handleDelete: true,
    });
  };

  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  return (
    <>
      
          <Box className="cart" sx={{ m: 2 }}>
            {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}
            {items.map((item) => (
              <Box
                display="flex"
                alignItems="flex-start"
                padding="1rem"
                key={item._id}
              >
                <Box className="image-container">
                  <img
                    src={item.image}
                    alt={item.name}
                    width="100%"
                    height="100%"
                  />
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  height="6rem"
                  paddingX="1rem"
                >
                  <div>{item.name}</div>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <ItemQuantity
                      isReadOnly={isReadOnly}
                      value={item.qty}
                      handleAdd={() => handleAdd(item)}
                      handleDelete={() => handleDelete(item)}
                    />
                    <Box padding="0.5rem" fontWeight="700">
                      ${item.cost}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
            <Box
              padding="1rem"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <h2>Order total</h2>
              </Box>
              <Box
                fontWeight="700"
                fontSize="1.5rem"
                data-testid="cart-total"
              >
                ${getTotalCartValue(items)}
              </Box>
            </Box>
            {isReadOnly ? (
              <></>
            ) : (
              <Box
                display="flex"
                justifyContent="flex-end"
                className="cart-footer"
              >
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<ShoppingCart />}
                  className="checkout-btn"
                  onClick={() => history.push("/checkout")}
                >
                  Checkout
                </Button>
              </Box>
            )}
          </Box>
        {isReadOnly ? (
          <>
              <Box className="cart" sx={{ m: 2 }}>
                <Box
                  paddingLeft="1rem"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <h2>Order Details</h2>
                </Box>

                {/*products*/}
                <Box
                  padding="1rem"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>Products</Box>
                  <Box>{getTotalItems(items)}</Box>
                </Box>

                {/*subtotal*/}
                <Box
                  padding="1rem"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box alignSelf="center">Subtotal</Box>
                  <Box alignSelf="center">${getTotalCartValue(items)}</Box>
                </Box>

                {/*shipping*/}
                <Box
                  padding="1rem"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>Shipping</Box>
                  <Box>${0}</Box>
                </Box>

                {/*total*/}
                <Box
                  padding="1rem"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box fontWeight="700" fontSize="1.5rem">
                    Total
                  </Box>
                  <Box fontWeight="700" fontSize="1.5rem">
                    ${getTotalCartValue(items)}
                  </Box>
                </Box>
              </Box>
          </>
        ) : (
          <></>
        )}
    </>
  );
};

export default Cart;
